import React, { Component } from 'react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { HomeIcon, CogIcon, CalendarIcon, UsersIcon, DeviceMobileIcon, LocationMarkerIcon, CheckCircleIcon, SearchIcon, AdjustmentsIcon, ClockIcon, BellIcon, InformationCircleIcon, ExclamationCircleIcon, RefreshIcon, XIcon, ChevronRightIcon } from '@heroicons/react/outline'
import Loader from '../UI/Loader'
import { generateArrivalsPDF } from '../Utils/PDFCreator'
import { motion } from 'framer-motion'

const ConfirmDialog = ({ isOpen, type, title, description, confirmLabel, onConfirm, onCancel, secondaryAction }) => {
	const iconMap = {
		warning: <ExclamationCircleIcon className="h-6 w-6 text-amber-500" />,
		danger:  <ExclamationCircleIcon className="h-6 w-6 text-red-500" />,
		info:    <InformationCircleIcon className="h-6 w-6 text-blue-500" />,
		success: <CheckCircleIcon className="h-6 w-6 text-emerald-500" />,
	}
	const iconBgMap = {
		warning: '#fffbeb',
		danger:  '#fef2f2',
		info:    '#eff6ff',
		success: '#ecfdf5',
	}
	const confirmBgMap = {
		warning: '#d97706',
		danger:  '#dc2626',
		info:    '#4f46e5',
		success: '#059669',
	}
	const confirmHoverMap = {
		warning: '#b45309',
		danger:  '#b91c1c',
		info:    '#4338ca',
		success: '#047857',
	}
	if (!isOpen)
		return null
	return (
		<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 200 }}>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				onClick={onCancel}
				style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)' }}
			/>
			<motion.div
				initial={{ scale: 0.9, opacity: 0, y: 20 }}
				animate={{ scale: 1, opacity: 1, y: 0 }}
				transition={{ duration: 0.18 }}
						className="relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
						style={{ maxWidth: '28rem', zIndex: 1 }}
					>
						<div className="p-6">
							<div className="flex items-start gap-4">
								<div className="p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: iconBgMap[type] }}>
									{iconMap[type]}
								</div>
								<div className="flex-1 min-w-0">
									<h3 className="text-base font-bold text-slate-900 leading-tight">{title}</h3>
									{description && (
										<p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{description}</p>
									)}
								</div>
								<button
									onClick={onCancel}
									className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors flex-shrink-0"
									style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
								>
									<XIcon className="h-5 w-5" />
								</button>
							</div>
						</div>
						<div className="px-6 py-4 flex items-center justify-end gap-3" style={{ backgroundColor: '#f8fafc' }}>
							<button
								onClick={onCancel}
								className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors rounded-lg"
								style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
							>
								Annuler
							</button>
							{secondaryAction && (
								<button
									onClick={secondaryAction.onClick}
									className="px-4 py-2 text-sm font-bold text-white rounded-xl transition-all shadow-md"
									style={{ backgroundColor: secondaryAction.color || '#4f46e5', backgroundImage: 'none' }}
								>
									{secondaryAction.label}
								</button>
							)}
							{confirmLabel && (
								<button
									onClick={onConfirm}
									className="px-5 py-2 text-sm font-bold text-white rounded-xl transition-all shadow-md"
									style={{ backgroundColor: confirmBgMap[type], backgroundImage: 'none' }}
									onMouseEnter={e => { e.currentTarget.style.backgroundColor = confirmHoverMap[type] }}
									onMouseLeave={e => { e.currentTarget.style.backgroundColor = confirmBgMap[type] }}
								>
									{confirmLabel}
								</button>
							)}
						</div>
				</motion.div>
		</div>
	)
}

const ARRIVAL_TIME_OPTIONS = (() => {
	const opts = []
	for (let h = 8; h < 24; h++)
	{
		opts.push({ value: h * 3600, label: `${String(h).padStart(2, '0')}:00` })
		opts.push({ value: h * 3600 + 1800, label: `${String(h).padStart(2, '0')}:30` })
	}
	opts.push({ value: 0, label: '00:00' })
	return opts
})()

const LANG_FLAGS = {
	fr: '🇫🇷 Français',
	en: '🇬🇧 English',
	de: '🇩🇪 Deutsch',
	nl: '🇳🇱 Nederlands',
	it: '🇮🇹 Italiano',
	es: '🇪🇸 Español',
	pt: '🇵🇹 Português'
}


class StayArrivals extends Component
{
	constructor(props)
	{
		super(props)
		const now = new Date()
		this.state = {
			loading: true,
			stays: [],
			day: String(now.getDate()).padStart(2, '0'),
			month: String(now.getMonth() + 1).padStart(2, '0'),
			year: String(now.getFullYear()),
			nav_center_day: String(now.getDate()).padStart(2, '0'),
			nav_center_month: String(now.getMonth() + 1).padStart(2, '0'),
			nav_center_year: String(now.getFullYear()),
			available_years: [String(now.getFullYear())],
			range_mode: false,
			range_start_day: String(now.getDate()).padStart(2, '0'),
			range_start_month: String(now.getMonth() + 1).padStart(2, '0'),
			range_start_year: String(now.getFullYear()),
			range_end_day: String(now.getDate()).padStart(2, '0'),
			range_end_month: String(now.getMonth() + 1).padStart(2, '0'),
			range_end_year: String(now.getFullYear()),
			modal_open: false,
			selected_stay: null,
			onsite_map: {},
			location_ready_map: {},
			jacuzzi_map: {},
			kit_double_map: {},
			kit_simple_map: {},
			baby_beds_map: {},
			arrival_dashboard_map: {},
			arr_set_by_map: {},
			location_map: {},
			edit_mode: false,
			confirm_msg: null,
			confirm_bold: null,
			confirm_type: 'success',
			copy_msg: null,
			onsite_pending: null,
			location_ready_pending: null,
			notif_pending_stay: null,
			notif_map: {},
			loaded_key: null,
			day_counts: {},
			qr_set: new Set(),
			search: '',
			kit_towels_map: {},
			baby_chairs_map: {},
			check_done_map: {},
			active_filters: {
				prepared: false,
				to_prepare: false,
				arrived: false,
				not_arrived: false,
				precheckin: false,
				no_precheckin: false,
				qr: false,
				user_app: false,
				late: false,
				with_options: false
			},
			show_filters: false,
			show_camping: true,
			show_location: true
		}
		this.pages = [
			{ name: 'Pré-checkins', href: '/checkins/arrivals', current: true }
		]
	}

	componentDidMount()
	{
		if (this.props.updateNavbarTitle)
			this.props.updateNavbarTitle('Planning des arrivées')
		if (!this.props.fetchArrivals)
		{
			this.setState({ loading: false })
			return ;
		}
		if (!this.props.config_checkins && this.props.fetchConfigCheckins)
			this.props.fetchConfigCheckins()
		if (!this.props.pitches && this.props.fetchPitches)
			this.props.fetchPitches()
		const { day, month, year } = this.state
		this._loadDay(`${year}-${month}-${day}`)
	}

	componentDidUpdate(prevProps, prevState)
	{
		const { day, month, year, range_mode, loading } = this.state
		if (loading)
			return ;
		const curr_key = `${year}-${month}-${day}`
		const prev_key = `${prevState.year}-${prevState.month}-${prevState.day}`
		if (prevState.range_mode !== range_mode)
		{
			if (range_mode)
				this._loadAll()
			else
				this._loadDay(curr_key)
			return ;
		}
		if (!range_mode && curr_key !== prev_key && this.state.loaded_key !== curr_key)
			this._loadDay(curr_key)
	}

	_buildMaps = (list) => {
		const years_set = new Set([String(new Date().getFullYear())])
		const onsite_map = {}
		const location_ready_map = {}
		const jacuzzi_map = {}
		const kit_double_map = {}
		const kit_simple_map = {}
		const baby_beds_map = {}
		const arrival_dashboard_map = {}
		const arr_set_by_map = {}
		const location_map = {}
		const notif_map = {}
		const kit_towels_map = {}
		const baby_chairs_map = {}
		const check_done_map = {}
		for (let i = 0; i < list.length; i++)
		{
			const item = list[i]
			if (list[i].stay_number)
				location_map[list[i].stay_number] = list[i].location || ''
			item.arrival_datestring = item.arrival_time
				? new Date(item.arrival_time * 1000).toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
				: ''
			if (item.arrival_time)
				years_set.add(item.arrival_datestring.substring(0, 4))
			if (list[i].stay_number)
			{
				const sn = list[i].stay_number
				const pc = list[i].precheckin || null
				onsite_map[sn] = !!(pc && pc.client_onsite)
				if (pc)
				{
					location_ready_map[sn] = !!pc.location_ready
					jacuzzi_map[sn] = !!pc.jacuzzi
					arrival_dashboard_map[sn] = pc.estimate_arrival_dashboard != null ? pc.estimate_arrival_dashboard : null
					arr_set_by_map[sn] = pc.est_arr_set_by || null
					notif_map[sn] = !!pc.location_ready_notified
					check_done_map[sn] = !!pc.check_done
				}
				// Repli séjour PMS -> precheckin champ par champ : un precheckin peut exister
				// sans jamais avoir été touché sur les kits (ex: soumission webapp), auquel cas
				// il ne faut pas masquer la valeur déjà importée sur le séjour.
				kit_double_map[sn] = (pc && pc.kit_double != null) ? pc.kit_double : (list[i].kit_double || 0)
				kit_simple_map[sn] = (pc && pc.kit_simple != null) ? pc.kit_simple : (list[i].kit_simple || 0)
				baby_beds_map[sn] = (pc && pc.baby_beds != null) ? pc.baby_beds : (list[i].baby_beds || 0)
				kit_towels_map[sn] = (pc && pc.kit_towels != null) ? pc.kit_towels : (list[i].kit_towels || 0)
				baby_chairs_map[sn] = (pc && pc.baby_chairs != null) ? pc.baby_chairs : (list[i].baby_chairs || 0)
			}
		}
		const qr_set = new Set()
		for (let i = 0; i < list.length; i++)
		{
			if (!list[i].cancelled && list[i].location && list[i].departure_time)
			{
				const d = new Date(list[i].departure_time * 1000)
					.toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
				qr_set.add(list[i].location + ':' + d)
			}
		}
		return {
			available_years: Array.from(years_set).sort(),
			qr_set,
			onsite_map, location_ready_map, jacuzzi_map,
			kit_double_map, kit_simple_map, baby_beds_map,
			arrival_dashboard_map, arr_set_by_map, location_map, notif_map,
			kit_towels_map, baby_chairs_map, check_done_map
		}
	}

	_loadDay = (key) => {
		this.setState({ loading: true })
		this.props.fetchArrivals(key)
		.then(data => {
			const list = Array.isArray(data) ? data : []
			const maps = this._buildMaps(list)
			const day_count = list.filter(s => !s.cancelled && s.arrival_datestring === key).length
			this.setState(prev => ({
				loading: false,
				stays: list,
				loaded_key: key,
				day_counts: { ...prev.day_counts, [key]: day_count },
				...maps
			}), () => this._loadWeekCounts())
		})
		.catch(() => {
			this.setState({ loading: false })
		})
	}

	_loadAll = () => {
		this.setState({ loading: true })
		this.props.fetchArrivals()
		.then(data => {
			const list = Array.isArray(data) ? data : []
			const maps = this._buildMaps(list)
			this.setState({ loading: false, stays: list, loaded_key: 'all', ...maps })
		})
		.catch(() => {
			this.setState({ loading: false })
		})
	}

	_getDayKey = (timestamp_seconds) => {
		if (!timestamp_seconds)
			return ''
		const date = new Date(timestamp_seconds * 1000)
		return date.toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
	}

	// Timestamp Unix (secondes) → "12/07/2026 à 14:35" (heure de Paris)
	_formatDateTime = (timestamp) => {
		if (!timestamp)
			return null
		const date = new Date(timestamp * 1000)
		const day = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })
		const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
		return `${day} à ${time}`
	}

	_formatTime = (seconds_since_midnight) => {
		if (!seconds_since_midnight && seconds_since_midnight !== 0)
			return '—'
		const h = Math.floor(seconds_since_midnight / 3600)
		const m = Math.floor((seconds_since_midnight % 3600) / 60)
		return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
	}

	handleDay = (event) => {
		this.setState({ day: event.target.value })
	}

	handleMonth = (event) => {
		this.setState({ month: event.target.value })
	}

	handleYear = (event) => {
		this.setState({ year: event.target.value })
	}

	VEHICLE_TYPES = {
		car: 'Voiture', van: 'Van', motorhome: 'Camping-car',
		campervan: 'Fourgon', caravan: 'Caravane', motorcycle: 'Moto', scooter: 'Scooter'
	}

	VEHICLE_COLORS = {
		white: 'Blanc', black: 'Noir', gray: 'Gris', silver: 'Argenté', beige: 'Beige',
		red: 'Rouge', blue: 'Bleu', green: 'Vert', yellow: 'Jaune', orange: 'Orange',
		brown: 'Marron', purple: 'Violet'
	}

	ID_TYPES = {
		passport: 'Passeport', id_card: 'Carte d\'identité', driving_license: 'Permis de conduire'
	}

	ANIMAL_TYPES = {
		dog: 'Chien', cat: 'Chat', other: 'Autre'
	}

	_getTodayParts = () => {
		const now = new Date()
		return {
			day: String(now.getDate()).padStart(2, '0'),
			month: String(now.getMonth() + 1).padStart(2, '0'),
			year: String(now.getFullYear())
		}
	}

	_shiftDay = (delta) => {
		const { day, month, year } = this.state
		const date = new Date(`${year}-${month}-${day}`)
		date.setDate(date.getDate() + delta)
		this.setState({
			day: String(date.getDate()).padStart(2, '0'),
			month: String(date.getMonth() + 1).padStart(2, '0'),
			year: String(date.getFullYear())
		})
	}

	_shiftRange = (delta) => {
		const { range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year } = this.state
		const start = new Date(`${range_start_year}-${range_start_month}-${range_start_day}`)
		const end = new Date(`${range_end_year}-${range_end_month}-${range_end_day}`)
		start.setDate(start.getDate() + delta)
		end.setDate(end.getDate() + delta)
		this.setState({
			range_start_day: String(start.getDate()).padStart(2, '0'),
			range_start_month: String(start.getMonth() + 1).padStart(2, '0'),
			range_start_year: String(start.getFullYear()),
			range_end_day: String(end.getDate()).padStart(2, '0'),
			range_end_month: String(end.getMonth() + 1).padStart(2, '0'),
			range_end_year: String(end.getFullYear())
		})
	}

	_shiftMonth = (delta) => {
		const { month, year, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year } = this.state
		let m = parseInt(month, 10) + delta
		let y = parseInt(year, 10)
		if (m < 1) { m = 12; y -= 1 }
		if (m > 12) { m = 1; y += 1 }
		const rs = new Date(`${range_start_year}-${range_start_month}-${range_start_day}`)
		rs.setMonth(rs.getMonth() + delta)
		const re = new Date(`${range_end_year}-${range_end_month}-${range_end_day}`)
		re.setMonth(re.getMonth() + delta)
		this.setState({
			month: String(m).padStart(2, '0'),
			year: String(y),
			range_start_day: String(rs.getDate()).padStart(2, '0'),
			range_start_month: String(rs.getMonth() + 1).padStart(2, '0'),
			range_start_year: String(rs.getFullYear()),
			range_end_day: String(re.getDate()).padStart(2, '0'),
			range_end_month: String(re.getMonth() + 1).padStart(2, '0'),
			range_end_year: String(re.getFullYear())
		})
	}

	_shiftWeek = (delta) => {
		const { nav_center_day, nav_center_month, nav_center_year } = this.state
		const date = new Date(parseInt(nav_center_year), parseInt(nav_center_month) - 1, parseInt(nav_center_day))
		date.setDate(date.getDate() + delta * 5)
		this.setState({
			nav_center_day: String(date.getDate()).padStart(2, '0'),
			nav_center_month: String(date.getMonth() + 1).padStart(2, '0'),
			nav_center_year: String(date.getFullYear())
		}, () => this._loadWeekCounts())
	}

	_loadWeekCounts = () => {
		const { stays, nav_center_day, nav_center_month, nav_center_year } = this.state
		if (!stays || stays.length === 0)
			return ;
		const center = new Date(parseInt(nav_center_year), parseInt(nav_center_month) - 1, parseInt(nav_center_day))
		const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
		const from = new Date(center)
		from.setDate(from.getDate() - 2)
		const to = new Date(center)
		to.setDate(to.getDate() + 2)
		const from_key = fmt(from)
		const to_key = fmt(to)
		const counts = {}
		for (let i = 0; i < stays.length; i++)
		{
			const s = stays[i]
			if (s.cancelled || !s.arrival_datestring)
				continue
			const k = s.arrival_datestring
			if (k >= from_key && k <= to_key)
				counts[k] = (counts[k] || 0) + 1
		}
		this.setState(prev => ({ day_counts: { ...prev.day_counts, ...counts } }))
	}

	_toggleLocationReady = (stay_number, current_value, location) => {
		const new_value = !current_value
		if (!new_value && this.state.onsite_map[stay_number])
		{
			this.setState({ location_ready_pending: { stay_number, location } })
			return ;
		}
		this.setState(prev => ({
			location_ready_map: { ...prev.location_ready_map, [stay_number]: new_value }
		}))
		if (!this.props.updateLocationReady)
			return ;
		this.props.updateLocationReady(stay_number, new_value)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = new_value
				? `Séjour #${stay_number} — emplacement marqué comme prêt`
				: `Séjour #${stay_number} — préparation annulée`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: new_value ? 'success' : 'error' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				location_ready_map: { ...prev.location_ready_map, [stay_number]: current_value }
			}))
		})
	}

	_confirmOnsite = () => {
		const { onsite_pending } = this.state
		if (!onsite_pending)
			return ;
		const { stay_number, location } = onsite_pending
		this.setState(prev => ({
			onsite_pending: null,
			location_ready_map: { ...prev.location_ready_map, [stay_number]: true }
		}))
		if (this.props.updateLocationReady)
			this.props.updateLocationReady(stay_number, true)
		this._doToggleOnsite(stay_number, location, `Séjour #${stay_number} — emplacement marqué comme prêt et client marqué comme arrivé`)
	}

	_doToggleOnsite = (stay_number, location, customText = null) => {
		this.setState(prev => ({
			onsite_map: { ...prev.onsite_map, [stay_number]: true }
		}))
		if (!this.props.updateOnsite)
			return ;
		this.props.updateOnsite(stay_number, true)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = customText || `Séjour #${stay_number} — client marqué comme arrivé`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: 'success' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				onsite_map: { ...prev.onsite_map, [stay_number]: false }
			}))
		})
	}

	_toggleOnsite = (stay_number, current_value, location) => {
		const new_value = !current_value
		if (new_value && !this.state.location_ready_map[stay_number])
		{
			this.setState({ onsite_pending: { stay_number, location } })
			return ;
		}
		this.setState(prev => ({
			onsite_map: { ...prev.onsite_map, [stay_number]: new_value }
		}))
		if (!this.props.updateOnsite)
			return ;
		this.props.updateOnsite(stay_number, new_value)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = new_value
				? `Séjour #${stay_number} — client marqué comme arrivé`
				: `Séjour #${stay_number} — arrivée annulée`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: new_value ? 'success' : 'error' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				onsite_map: { ...prev.onsite_map, [stay_number]: current_value }
			}))
		})
	}

	_toggleJacuzzi = (stay_number, current_value, location) => {
		const new_value = !current_value
		this.setState(prev => ({
			jacuzzi_map: { ...prev.jacuzzi_map, [stay_number]: new_value }
		}))
		if (!this.props.updateJacuzzi)
			return ;
		this.props.updateJacuzzi(stay_number, new_value)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = new_value
				? `Séjour #${stay_number} — jacuzzi préparé`
				: `Séjour #${stay_number} — jacuzzi annulé`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: new_value ? 'success' : 'error' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				jacuzzi_map: { ...prev.jacuzzi_map, [stay_number]: current_value }
			}))
		})
	}

	_saveLinenField = (stay_number, field, value, location) => {
		const num = parseInt(value, 10)
		const safe = isNaN(num) ? 0 : Math.max(0, num)
		const map_key = field + '_map'
		const current = this.state[map_key][stay_number] != null ? this.state[map_key][stay_number] : 0
		this.setState(prev => ({
			[map_key]: { ...prev[map_key], [stay_number]: safe }
		}))
		if (safe === current)
			return ;
		if (!this.props.updateLinenField)
			return ;
		this.props.updateLinenField(stay_number, field, safe)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const LABELS = { kit_double: 'kits draps double', kit_simple: 'kits draps simples', baby_beds: 'lits bébés', kit_towels: 'kits serviettes', baby_chairs: 'chaises bébés' }
			const msg = `Séjour #${stay_number} — ${LABELS[field]} : ${safe}`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: 'success' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			// revert on error
		})
	}

	_saveArrivalDashboard = (stay_number, time_str, location) => {
		const value = time_str === '' ? null : (() => {
			const parts = time_str.split(':')
			return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60
		})()
		const current = this.state.arrival_dashboard_map[stay_number] != null ? this.state.arrival_dashboard_map[stay_number] : null
		if (value === current)
			return ;
		const current_set_by = this.state.arr_set_by_map[stay_number] || null
		this.setState(prev => ({
			arrival_dashboard_map: { ...prev.arrival_dashboard_map, [stay_number]: value },
			arr_set_by_map: { ...prev.arr_set_by_map, [stay_number]: 'dashboard' }
		}))
		if (!this.props.updateArrivalDashboard)
			return ;
		this.props.updateArrivalDashboard(stay_number, value)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = value === null
				? `Séjour #${stay_number} — heure d'arrivée dashboard effacée`
				: `Séjour #${stay_number} — heure d'arrivée dashboard : ${this._formatTime(value)}`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: 'success' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				arrival_dashboard_map: { ...prev.arrival_dashboard_map, [stay_number]: current },
				arr_set_by_map: { ...prev.arr_set_by_map, [stay_number]: current_set_by }
			}))
		})
	}

	_saveLocation = (stay_number, value, current) => {
		const safe = value === '' ? '' : value
		if (safe === current)
			return ;
		this.setState(prev => ({
			location_map: { ...prev.location_map, [stay_number]: safe }
		}))
		if (!this.props.updateLocation)
			return ;
		this.props.updateLocation(stay_number, safe === '' ? null : safe)
		.then(() => {
			const bold = safe ? `Empl. ${safe}` : null
			const msg = safe === ''
				? `Séjour #${stay_number} — emplacement effacé`
				: `Séjour #${stay_number} — emplacement mis à jour : ${safe}`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: 'success' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				location_map: { ...prev.location_map, [stay_number]: current }
			}))
		})
	}

	_search_timer = null

	_onKitChange = (stay_number, field, value) => {
		const map_key = field + '_map'
		this.setState(prev => ({ [map_key]: { ...prev[map_key], [stay_number]: value } }))
	}

	_toggleCheckDone = (stay_number, new_value, location) => {
		this.setState(prev => ({
			check_done_map: { ...prev.check_done_map, [stay_number]: new_value }
		}))
		if (!this.props.updateCheckDone)
			return ;
		this.props.updateCheckDone(stay_number, new_value)
		.then(() => {
			const bold = location ? `Empl. ${location}` : null
			const msg = `Séjour #${stay_number} — ${new_value ? 'marqué comme traité' : 'traitement annulé'}`
			this.setState({ confirm_bold: bold, confirm_msg: msg, confirm_type: new_value ? 'success' : 'error' })
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
		})
		.catch(() => {
			this.setState(prev => ({
				check_done_map: { ...prev.check_done_map, [stay_number]: !new_value }
			}))
		})
	}

	_toggleFilter = (key) => {
		this.setState(prev => {
			const was_active = prev.active_filters[key]
			const reset = Object.keys(prev.active_filters).reduce((acc, k) => { acc[k] = false; return acc }, {})
			return { active_filters: { ...reset, [key]: !was_active } }
		})
	}

	_resetFilters = () => {
		this.setState({
			active_filters: {
				prepared: false,
				to_prepare: false,
				arrived: false,
				not_arrived: false,
				precheckin: false,
				no_precheckin: false,
				qr: false,
				user_app: false,
				late: false,
				with_options: false
			}
		})
	}

	_onRequestNotify = (stay) => {
		this.setState({ notif_pending_stay: stay })
	}

	openModal = (stay) => {
		this.setState({ modal_open: true, selected_stay: stay })
	}

	closeModal = () => {
		this.setState({ modal_open: false })
	}

	_copyButton = (copyValue) => {
		if (!copyValue)
			return null
		return (
			<button
				onClick={() => {
					navigator.clipboard.writeText(copyValue)
					this.setState({ copy_msg: copyValue })
					if (this._copy_timer)
						clearTimeout(this._copy_timer)
					this._copy_timer = setTimeout(() => this.setState({ copy_msg: null }), 2000)
				}}
				title="Copier"
				style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1 }}
				className="ml-2 text-gray-400 hover:text-indigo-600"
			>
				<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
				</svg>
			</button>
		)
	}

	_renderRow = (label, value, copyValue) => {
		return (
			<div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
				<dt className="text-sm font-medium text-gray-500">{label}</dt>
				<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
					<span>{value || '—'}</span>
					{copyValue !== undefined ? this._copyButton(copyValue || value) : null}
				</dd>
			</div>
		)
	}

	_renderRowAlt = (label, value, copyValue) => {
		return (
			<div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
				<dt className="text-sm font-medium text-gray-500">{label}</dt>
				<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
					<span>{value || '—'}</span>
					{copyValue !== undefined ? this._copyButton(copyValue || value) : null}
				</dd>
			</div>
		)
	}

	_renderLocationReadyConfirm = () => {
		const { location_ready_pending } = this.state
		const cancel = () => this.setState({ location_ready_pending: null })
		const uncheckBoth = () => {
			const { stay_number, location } = location_ready_pending
			const bold = location ? `Empl. ${location}` : null
			this.setState(prev => ({
				location_ready_pending: null,
				location_ready_map: { ...prev.location_ready_map, [stay_number]: false },
				onsite_map: { ...prev.onsite_map, [stay_number]: false },
				confirm_bold: bold,
				confirm_msg: `Séjour #${stay_number} — préparation annulée et arrivée annulée`,
				confirm_type: 'error'
			}))
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
			if (this.props.updateLocationReady)
				this.props.updateLocationReady(stay_number, false)
			if (this.props.updateOnsite)
				this.props.updateOnsite(stay_number, false)
		}
		const uncheckLocationOnly = () => {
			const { stay_number, location } = location_ready_pending
			const bold = location ? `Empl. ${location}` : null
			this.setState(prev => ({
				location_ready_pending: null,
				location_ready_map: { ...prev.location_ready_map, [stay_number]: false },
				confirm_bold: bold,
				confirm_msg: `Séjour #${stay_number} — préparation annulée`,
				confirm_type: 'error'
			}))
			if (this._confirm_timer)
				clearTimeout(this._confirm_timer)
			this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null, confirm_bold: null }), 6000)
			if (this.props.updateLocationReady)
				this.props.updateLocationReady(stay_number, false)
		}
		return (
			<ConfirmDialog
				isOpen={!!location_ready_pending}
				type="danger"
				title="Annuler la préparation"
				description="Cet emplacement est aussi marqué comme Arrivé. Que souhaitez-vous annuler ?"
				confirmLabel="Préparation + Arrivée"
				onConfirm={uncheckBoth}
				onCancel={cancel}
				secondaryAction={{ label: 'Préparation uniquement', onClick: uncheckLocationOnly, color: '#4f46e5' }}
			/>
		)
	}

	_renderOnsiteConfirm = () => {
		const { onsite_pending } = this.state
		const cancel = () => this.setState({ onsite_pending: null })
		return (
			<ConfirmDialog
				isOpen={!!onsite_pending}
				type="warning"
				title="Confirmer l'arrivée"
				description="L'emplacement n'est pas encore marqué Prêt. Confirmer quand même l'arrivée du client ?"
				confirmLabel="Oui, confirmer l'arrivée"
				onConfirm={this._confirmOnsite}
				onCancel={cancel}
			/>
		)
	}

	_renderNotifConfirm = () => {
		const { notif_pending_stay } = this.state
		const cancel = () => this.setState({ notif_pending_stay: null })
		const confirm = () => {
			const stay = notif_pending_stay
			this.setState({ notif_pending_stay: null })
			if (!this.props.notifyLocationReady)
				return ;
			this.props.notifyLocationReady(stay.client_number, stay.stay_number)
			.then(ret => {
				const sent = ret && ret.sent_to > 0 ? ret.sent_to : 0
				const bold = stay.location ? `Empl. ${stay.location}` : null
				const pc = stay.precheckin
				const participant_emails = pc && Array.isArray(pc.participants)
					? pc.participants.filter(p => p && p.email && p.email.trim()).length
					: 0
				const email_count = participant_emails > 0
					? participant_emails
					: ((pc && pc.email && pc.email.trim()) ? 1 : ((stay.client && stay.client.email1 && stay.client.email1.trim()) ? 1 : 0))
				const parts = []
				if (sent > 0)
					parts.push(`${sent} notification${sent > 1 ? 's' : ''} app`)
				if (email_count > 0)
					parts.push(`${email_count} e-mail${email_count > 1 ? 's' : ''}`)
				const msg = parts.length > 0
					? `Séjour #${stay.stay_number} — ${parts.join(', ')} envoyé${parts.length > 1 || (sent > 1 || email_count > 1) ? 's' : ''}`
					: `Séjour #${stay.stay_number} — aucun destinataire trouvé`
				this.setState(prev => ({
					confirm_msg: msg,
					confirm_bold: bold,
					confirm_type: parts.length > 0 ? 'success' : 'error',
					notif_map: { ...prev.notif_map, [stay.stay_number]: true }
				}))
				if (this._confirm_timer)
					clearTimeout(this._confirm_timer)
				this._confirm_timer = setTimeout(() => this.setState({ confirm_msg: null }), 6000)
			})
			.catch(() => {})
		}
		return (
			<ConfirmDialog
				isOpen={!!notif_pending_stay}
				type="info"
				title="Notifier le client"
				description="Envoyer une notification au client par e-mail et webpush pour l'avertir que son emplacement est prêt ?"
				confirmLabel="Envoyer une notification"
				onConfirm={confirm}
				onCancel={cancel}
			/>
		)
	}

	_renderModal = () => {
		const { modal_open, selected_stay } = this.state
		if (!selected_stay)
			return null
		const stay = selected_stay
		const client = stay.client || {}
		const pc = stay.precheckin || null
		const participants = pc ? (pc.participants || []) : (stay.participants || [])
		const vehicles = pc ? (pc.vehicles || []) : []
		const animals = pc ? (pc.animals || []) : []
		const show_participants = !(this.props.config_checkins && this.props.config_checkins.participants === false)
		const show_vehicles = !(this.props.config_checkins && this.props.config_checkins.vehicles === false)
		const show_animals = !(this.props.config_checkins && this.props.config_checkins.animals === false)

		const civility_src = pc ? pc.civility : client.civility
		const civility_map = { woman: 'Madame', man: 'Monsieur', other: 'Autre' }
		const civility_label = civility_map[civility_src] || civility_src || null

		const lastname = (pc && pc.lastname) || client.lastname || null
		const firstname = (pc && pc.firstname) || client.firstname || null
		const birthday = (pc && pc.birthday) || client.birthday || null
		const email = (pc && pc.email) || client.email1 || client.email2 || null
		const phone = (pc && pc.phone) || client.phone1 || client.phone2 || client.phone3 || null
		const street = (pc && pc.street) || client.street1 || null
		const zipcode = (pc && pc.zipcode) || client.zipcode || null
		const city = (pc && pc.city) || client.city || null
		const country = (pc && pc.country) || client.country || null
		const place_of_birth = (pc && pc.place_of_birth) || client.place_of_birth || null
		const lang_code = (pc && pc.lang) || client.langage || null
		const lang_label = lang_code ? (LANG_FLAGS[lang_code] || lang_code) : null
		const id_info = pc && pc.id_type
			? `${this.ID_TYPES[pc.id_type] || pc.id_type}${pc.id_number ? ' — ' + pc.id_number : ''}`
			: null
		// date de dépôt du formulaire webapp ; fallback created_time pour les
		// anciens docs (uniquement si le formulaire a bien été rempli)
		const submitted_time = pc
			? (pc.submitted_time || (pc.lastname ? pc.created_time : null))
			: null

		return (
			<Transition appear show={modal_open} as={Fragment}>
				<Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={this.closeModal}>
					<div className="flex items-start justify-center min-h-screen pt-10 pb-10 px-4 text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<div className="inline-block align-top bg-white rounded-lg text-left shadow-xl transform transition-all my-8 sm:align-middle sm:max-w-2xl w-full">
								<div className="px-4 py-5 sm:px-6 border-b border-gray-200">
									<h3 className="text-lg leading-6 font-medium text-gray-900 my-0">
										Détails de l'arrivée
									</h3>
									<p className="mt-1 text-sm text-gray-500 mb-0">
										{stay.location_category || ''}{stay.location ? ` — Empl. ${stay.location}` : ''}
										{pc && pc.created_by_user && (
											<span className="block text-xs text-gray-400 mt-0.5">
												Soumis par {[pc.created_by_user.firstname, pc.created_by_user.lastname].filter(Boolean).join(' ') || pc.created_by_user.email || '—'}
											</span>
										)}
										{submitted_time && (
											<span className="block text-xs text-gray-400 mt-0.5">
												Formulaire déposé le {this._formatDateTime(submitted_time)}
											</span>
										)}
									</p>
								</div>
								<div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>

									{/* Coordonnées client */}
									<div className="px-4 pt-4 pb-1 sm:px-6">
										<h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-0">Coordonnées</h4>
									</div>
									<dl>
										{this._renderRow('Civilité', civility_label, civility_label)}
										{this._renderRowAlt('Nom', lastname, lastname)}
										{this._renderRow('Prénom', firstname, firstname)}
										{this._renderRowAlt('Date de naissance', birthday, birthday)}
										{this._renderRow('Lieu de naissance', place_of_birth, place_of_birth)}
										{this._renderRowAlt('Email', email, email)}
										{this._renderRow('Téléphone', phone, phone)}
										{lang_label && this._renderRowAlt('Langue', lang_label, null)}
										{this._renderRow('Voie', street, street)}
										{this._renderRowAlt('Code postal', zipcode, zipcode)}
										{this._renderRow('Commune', city, city)}
										{this._renderRowAlt('Pays', country, country)}
										{id_info && this._renderRowAlt('Pièce d\'identité', id_info, pc && pc.id_number)}
										{this._renderRow('N° client', stay.client_number ? String(stay.client_number) : null, stay.client_number ? String(stay.client_number) : null)}
									</dl>

									{/* Séjour */}
									<div className="px-4 pt-5 pb-1 sm:px-6">
										<h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-0">Séjour</h4>
									</div>
									<dl>
										{this._renderRow('N° séjour', stay.stay_number ? String(stay.stay_number) : null, stay.stay_number ? String(stay.stay_number) : null)}
										{this._renderRowAlt('Arrivée', stay.arrival, stay.arrival)}
										{this._renderRow('Départ', stay.departure, stay.departure)}
										{this._renderRowAlt('Hébergement', stay.location_category, stay.location_category)}
										{this._renderRow('Emplacement', stay.location, stay.location)}
										{pc && pc.estimate_arrival && this._renderRowAlt('Heure d\'arrivée estimée', pc.estimate_arrival, pc.estimate_arrival)}
									</dl>

									{/* Participants — depuis pc ou depuis stay si pas de pc */}
									{show_participants && (pc || participants.length > 0) && (<>
										<div className="px-4 pt-5 pb-1 sm:px-6">
											<h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-0">
												Participants{participants.length > 0 ? ` (${participants.length})` : ''}
											</h4>
										</div>
										{participants.length === 0
											? (
												<p className="px-6 py-2 text-sm text-gray-400">Aucun participant renseigné</p>
											)
											: participants.map((participant, k) => (
												<div key={k} className="border-t border-gray-100 mx-6 mt-2 pt-2 pb-2">
													<p className="text-sm font-medium text-gray-700 mb-0 flex items-center">
														{participant.firstname || ''} {participant.lastname || ''}
														{(participant.firstname || participant.lastname) && this._copyButton([participant.firstname, participant.lastname].filter(Boolean).join(' '))}
													</p>
													{participant.birthday && (
														<p className="text-xs text-gray-500 mt-0 mb-0 flex items-center">Né(e) le {participant.birthday}{this._copyButton(participant.birthday)}</p>
													)}
													{participant.email && (
														<p className="text-xs text-gray-500 mt-0 mb-0 flex items-center">{participant.email}{this._copyButton(participant.email)}</p>
													)}
												</div>
											))
										}
									</>)}

									{/* Véhicules et animaux — uniquement si pc existant */}
									{pc && (
										<>
										{show_vehicles && (<>
											{/* Véhicules */}
											<div className="px-4 pt-5 pb-1 sm:px-6">
												<h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-0">
													Véhicules{vehicles.length > 0 ? ` (${vehicles.length})` : ''}
												</h4>
											</div>
											{vehicles.length === 0
												? (
													<p className="px-6 py-2 text-sm text-gray-400">Aucun véhicule renseigné</p>
												)
												: vehicles.map((vehicle, k) => (
													<div key={k} className="border-t border-gray-100 mx-6 mt-2 pt-2 pb-2">
														<p className="text-sm font-medium text-gray-700 mb-0 flex items-center gap-2">
															<span>{vehicle.plate || '—'}</span>
															{vehicle.country ? <span className="text-xs text-gray-500">({vehicle.country})</span> : null}
															{vehicle.plate && (
																<button
																	onClick={() => {
																		navigator.clipboard.writeText(vehicle.plate)
																		this.setState({ copy_msg: vehicle.plate })
																		if (this._copy_timer)
																			clearTimeout(this._copy_timer)
																		this._copy_timer = setTimeout(() => this.setState({ copy_msg: null }), 2000)
																	}}
																	title="Copier la plaque"
																	className="ml-1 text-gray-400 hover:text-indigo-600"
																	style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1 }}
																>
																	<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
																		<path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
																	</svg>
																</button>
															)}
														</p>
														{(vehicle.vehicle_type || vehicle.brand || vehicle.color) && (
															<p className="text-xs text-gray-500 mt-0 mb-0">
																{[this.VEHICLE_TYPES[vehicle.vehicle_type] || vehicle.vehicle_type, vehicle.brand === 'other' ? (vehicle.brand_other || 'Autre') : vehicle.brand, this.VEHICLE_COLORS[vehicle.color] || vehicle.color].filter(Boolean).join(' · ')}
															</p>
														)}
													</div>
												))
											}

										</>)}
										{show_animals && (<>
											{/* Animaux */}
											<div className="px-4 pt-5 pb-1 sm:px-6">
												<h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-0">
													Animaux{animals.length > 0 ? ` (${animals.length})` : ''}
												</h4>
											</div>
											{animals.length === 0
												? (
													<p className="px-6 py-2 pb-4 text-sm text-gray-400">Aucun animal renseigné</p>
												)
												: animals.map((animal, k) => (
													<div key={k} className="border-t border-gray-100 mx-6 mt-2 pt-2 pb-2">
														<p className="text-sm font-medium text-gray-700 mb-0">
															<span className="flex items-center gap-1">{animal.name || '—'}{animal.name && this._copyButton(animal.name)}</span>
															<span className="flex items-center gap-1 ml-2 text-xs text-gray-500">
																{(() => {
																	const typeLabel = animal.type === 'other' ? (animal.type_other || 'Autre') : (this.ANIMAL_TYPES[animal.type] || animal.type)
																	return <>{typeLabel}{typeLabel && this._copyButton(typeLabel)}</>
																})()}
															</span>
														</p>
														{animal.weight && (
															<p className="text-xs text-gray-500 mt-0 mb-0 flex items-center">Poids : {parseFloat(animal.weight).toFixed(1)} kg{this._copyButton(parseFloat(animal.weight).toFixed(1))}</p>
														)}
														{animal.chip && (
															<p className="text-xs text-gray-500 mt-0 mb-0 flex items-center">Puce : {animal.chip}{this._copyButton(animal.chip)}</p>
														)}
														{animal.last_vaccine && (
															<p className="text-xs text-gray-500 mt-0 mb-0 flex items-center">Dernier vaccin : {animal.last_vaccine}{this._copyButton(animal.last_vaccine)}</p>
														)}
													</div>
												))
											}
										</>)}
										</>
									)}
									<div className="pb-4" />
								</div>
								<div className="px-4 py-3 sm:px-6 border-t border-gray-200 flex justify-end">
									<button
										type="button"
										className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
										onClick={this.closeModal}
									>
										Fermer
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		)
	}

	_downloadPdf = () => {
		const { stays, day, month, year, range_mode, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year, location_ready_map, onsite_map, jacuzzi_map, kit_double_map, kit_simple_map, baby_beds_map, arrival_dashboard_map, arr_set_by_map, kit_towels_map, baby_chairs_map } = this.state
		const show_jacuzzi = !!(this.props.config_checkins && this.props.config_checkins.jacuzzi)
		const show_kits = !!(this.props.config_checkins && this.props.config_checkins.kits)
		const _lastname = (s) => ((s.precheckin && s.precheckin.lastname) || (s.client && s.client.lastname) || '').toLowerCase()
		const _eff_arrival = (s) => {
			const ead = arrival_dashboard_map[s.stay_number]
			if (arr_set_by_map[s.stay_number] === 'dashboard' && ead != null)
				return ead
			const ea = s.precheckin && s.precheckin.estimate_arrival
			if (ea)
				return ea
			return ead != null ? ead : null
		}
		let filtered = []
		let dateLabel = ''
		if (range_mode)
		{
			const start_key = `${range_start_year}-${range_start_month}-${range_start_day}`
			const end_key = `${range_end_year}-${range_end_month}-${range_end_day}`
			filtered = stays
				.filter(s => {
					if (s.cancelled)
						return false
					const k = s.arrival_datestring || ''
					return k >= start_key && k <= end_key
				})
				.sort((a, b) => {
					const ak = a.arrival_datestring || ''
					const bk = b.arrival_datestring || ''
					if (ak !== bk)
						return ak < bk ? -1 : 1
					const a_has_pc = !!a.precheckin
					const b_has_pc = !!b.precheckin
					if (a_has_pc !== b_has_pc)
						return a_has_pc ? -1 : 1
					const a_eff = _eff_arrival(a)
					const b_eff = _eff_arrival(b)
					if (a_eff !== null && b_eff === null)
						return -1
					if (a_eff === null && b_eff !== null)
						return 1
					if (a_eff !== null && b_eff !== null && a_eff !== b_eff)
						return a_eff - b_eff
					return _lastname(a).localeCompare(_lastname(b))
				})
			const fmt = (d, m, y) => `${d}/${m}/${y}`
			dateLabel = `${fmt(range_start_day, range_start_month, range_start_year)} — ${fmt(range_end_day, range_end_month, range_end_year)}`
		}
		else
		{
			const current_day_key = `${year}-${month}-${day}`
			filtered = stays
				.filter(s => !s.cancelled && s.arrival_datestring === current_day_key)
				.sort((a, b) => {
					const a_has_pc = !!a.precheckin
					const b_has_pc = !!b.precheckin
					if (a_has_pc !== b_has_pc)
						return a_has_pc ? -1 : 1
					const a_eff = _eff_arrival(a)
					const b_eff = _eff_arrival(b)
					if (a_eff !== null && b_eff === null)
						return -1
					if (a_eff === null && b_eff !== null)
						return 1
					if (a_eff !== null && b_eff !== null && a_eff !== b_eff)
						return a_eff - b_eff
					return _lastname(a).localeCompare(_lastname(b))
				})
			dateLabel = `${day}/${month}/${year}`
		}
		const qr_set = this.state.qr_set
		const { show_camping, show_location } = this.state
		if (!show_camping)
			filtered = filtered.filter(s => s.stay_type !== 'Camping')
		if (!show_location)
			filtered = filtered.filter(s => s.stay_type !== 'Location')
		// try/catch diagnostic : certains postes (Windows) ont eu des exports
		// muets — on remonte l'erreur réelle au lieu d'échouer en silence
		try
		{
			generateArrivalsPDF(filtered, dateLabel, this._formatTime.bind(this), qr_set, location_ready_map, onsite_map, jacuzzi_map, show_jacuzzi, kit_double_map, kit_simple_map, baby_beds_map, show_kits, arrival_dashboard_map, true, kit_towels_map, baby_chairs_map)
		}
		catch (err)
		{
			console.error('export PDF arrivées', err)
			window.alert('Export PDF impossible : ' + (err && err.message ? err.message : err))
		}
	}

	_renderStats = () => {
		const { stays, range_mode, location_ready_map, qr_set, day, month, year, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year } = this.state
		const current_day_key = `${year}-${month}-${day}`
		const start_key = `${range_start_year}-${range_start_month}-${range_start_day}`
		const end_key = `${range_end_year}-${range_end_month}-${range_end_day}`
		let total = 0, nb_prechecked = 0, nb_ready = 0, nb_qr = 0
		for (let i = 0; i < stays.length; i++)
		{
			const s = stays[i]
			if (s.cancelled)
				continue
			if (range_mode)
			{
				const k = s.arrival_datestring || ''
				if (k < start_key || k > end_key)
					continue
			}
			else if (s.arrival_datestring !== current_day_key)
				continue
			total++
			if (s.precheckin && (s.precheckin.estimate_arrival || (s.precheckin.participants && s.precheckin.participants.length > 0)))
				nb_prechecked++
			if (location_ready_map[s.stay_number])
				nb_ready++
			if (s.location && qr_set.has(s.location + ':' + (range_mode ? (s.arrival_datestring || '') : current_day_key)))
				nb_qr++
		}
		const nb_remaining = total - nb_ready
		const pct_pc = total > 0 ? Math.round((nb_prechecked / total) * 100) : 0
		const pct_qr = total > 0 ? Math.round((nb_qr / total) * 100) : 0
		const total_subtitle = range_mode ? 'Sur la plage sélectionnée' : "Prévu aujourd'hui"
		const qr_subtitle = range_mode ? `${pct_qr}% des arrivées de la période` : `${pct_qr}% des arrivées du jour`
		const stat_list = [
			{ label: 'Total arrivées', value: total, total_label: null, Icon: UsersIcon, color: 'indigo', progress: 100, subtitle: total_subtitle },
			{ label: 'Pré-checkins', value: nb_prechecked, total_label: `/ ${total}`, Icon: DeviceMobileIcon, color: 'sky', progress: pct_pc, subtitle: `${pct_pc}% de taux de retour` },
			{ label: 'Hébergements prêts', value: nb_ready, total_label: `/ ${total}`, Icon: LocationMarkerIcon, color: 'emerald', progress: total > 0 ? Math.round((nb_ready / total) * 100) : 0, subtitle: `${nb_remaining} restant${nb_remaining !== 1 ? 's' : ''} à préparer` },
			{ label: 'Quitté - repris', value: nb_qr, total_label: `/ ${total}`, Icon: CheckCircleIcon, color: 'amber', progress: total > 0 ? Math.round((nb_qr / total) * 100) : 0, subtitle: qr_subtitle }
		]
		return (
			<div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
				{stat_list.map((stat, i) => (
					<div key={i} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between h-[160px]">
						<div className="flex items-start justify-between">
							<div className="flex flex-col gap-1">
								<p className="text-[11px] font-bold text-slate-800 tracking-widest uppercase leading-tight">{stat.label}</p>
								<div className="flex items-baseline gap-1.5 mt-2">
									<span className="text-2xl font-black text-[#1F2937] tracking-tight leading-none">{stat.value}</span>
									{stat.total_label && (
										<span className="text-sm font-bold text-gray-300 leading-none">{stat.total_label}</span>
									)}
								</div>
							</div>
							<div className={`p-2.5 rounded-xl ${
								stat.color === 'indigo' ? 'bg-indigo-50 text-indigo-500' :
								stat.color === 'sky' ? 'bg-sky-50 text-sky-500' :
								stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-500' :
								stat.color === 'amber' ? 'bg-amber-50 text-amber-500' : ''
							}`}>
								<stat.Icon className="h-[18px] w-[18px]" />
							</div>
						</div>
						<div>
							<p className="text-[11px] text-gray-400 my-3">{stat.subtitle}</p>
							<div className="w-full h-1.5 bg-gray-50 rounded-full overflow-hidden">
								<div
									className={`h-full rounded-full ${
										stat.color === 'indigo' ? 'bg-indigo-500' :
										stat.color === 'sky' ? 'bg-sky-500' :
										stat.color === 'emerald' ? 'bg-emerald-500' :
										stat.color === 'amber' ? 'bg-amber-500' : ''
									}`}
								style={{ width: `${stat.progress}%` }}
								/>
							</div>
						</div>
					</div>
				))}
				<Link to={{ pathname: '/checkins/stats', state: { date: range_mode ? start_key : current_day_key } }} className="flex flex-col justify-between h-[160px] p-5 rounded-xl shadow-sm transition-all group no-underline" style={{ backgroundColor: '#4f46e5' }}
					onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#4338ca' }}
					onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#4f46e5' }}
				>
					<div className="flex justify-between items-start">
						<span className="text-[10px] font-black uppercase tracking-widest leading-none" style={{ color: '#c7d2fe' }}>Analyse</span>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c7d2fe" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform">
							<polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
							<polyline points="17 6 23 6 23 12" />
						</svg>
					</div>
					<div className="flex items-center justify-between text-white">
						<span className="text-sm font-black tracking-tight">Pics d'affluence</span>
						<ChevronRightIcon className="h-4 w-4 text-white" />
					</div>
				</Link>
			</div>
		)
	}

	_renderTable = () => {
		const { stays, day, month, year, range_mode, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year, onsite_map, location_ready_map, jacuzzi_map, kit_double_map, kit_simple_map, baby_beds_map, arrival_dashboard_map, arr_set_by_map, location_map, notif_map, confirm_msg, confirm_bold, confirm_type } = this.state
		const pitch_list = this.props.pitches || []
		const show_jacuzzi = !!(this.props.config_checkins && this.props.config_checkins.jacuzzi)
		const show_kits = !!(this.props.config_checkins && this.props.config_checkins.kits)
		const show_pms_check = !!(this.props.config_pms && this.props.config_pms.values && this.props.config_pms.values.pms_check)
		const edit_mode = this.state.edit_mode
		const qr_set = this.state.qr_set
		const current_day_key = `${year}-${month}-${day}`
		const _lastname = (s) => ((s.precheckin && s.precheckin.lastname) || (s.client && s.client.lastname) || '').toLowerCase()
		const _eff_arrival = (s) => {
			const ead = arrival_dashboard_map[s.stay_number]
			if (arr_set_by_map[s.stay_number] === 'dashboard' && ead != null)
				return ead
			const ea = s.precheckin && s.precheckin.estimate_arrival
			if (ea)
				return ea
			return ead != null ? ead : null
		}
		const _sort = (a, b) => {
			if (range_mode)
			{
				const ak = a.arrival_datestring || ''
				const bk = b.arrival_datestring || ''
				if (ak !== bk)
					return ak < bk ? -1 : 1
			}
			const a_has_pc = !!a.precheckin
			const b_has_pc = !!b.precheckin
			if (a_has_pc !== b_has_pc)
				return a_has_pc ? -1 : 1
			const a_eff = _eff_arrival(a)
			const b_eff = _eff_arrival(b)
			if (a_eff !== null && b_eff === null)
				return -1
			if (a_eff === null && b_eff !== null)
				return 1
			if (a_eff !== null && b_eff !== null && a_eff !== b_eff)
				return a_eff - b_eff
			return _lastname(a).localeCompare(_lastname(b))
		}
		const search_term = this.state.search.toLowerCase().trim()
		let filtered = range_mode
			? stays
				.filter(s => {
					if (s.cancelled)
						return false
					const k = s.arrival_datestring || ''
					const start_key = `${range_start_year}-${range_start_month}-${range_start_day}`
					const end_key = `${range_end_year}-${range_end_month}-${range_end_day}`
					return k >= start_key && k <= end_key
				})
				.sort(_sort)
			: stays
				.filter(s => !s.cancelled && s.arrival_datestring === current_day_key)
				.sort(_sort)
		if (search_term)
		{
			filtered = filtered.filter(s => {
				const lastname = ((s.precheckin && s.precheckin.lastname) || (s.client && s.client.lastname) || '').toLowerCase()
				const firstname = ((s.precheckin && s.precheckin.firstname) || (s.client && s.client.firstname) || '').toLowerCase()
				const location = (s.location || '').toLowerCase()
				const category = (s.location_category || '').toLowerCase()
				const email = ((s.precheckin && s.precheckin.email) || (s.client && s.client.email) || '').toLowerCase()
				const stay_num = s.stay_number ? String(s.stay_number) : ''
				const client_num = s.client_number ? String(s.client_number) : ''
				return lastname.includes(search_term) || firstname.includes(search_term) || location.includes(search_term) || category.includes(search_term) || email.includes(search_term) || stay_num.includes(search_term) || client_num.includes(search_term)
			})
		}
		const _is_real_pc = (s) => s.precheckin && (s.precheckin.estimate_arrival || (s.precheckin.participants && s.precheckin.participants.length > 0))
		const { active_filters, show_filters } = this.state
		const active_filter_count = Object.values(active_filters).filter(Boolean).length
		if (active_filter_count > 0)
		{
			filtered = filtered.filter(s => {
				const sn = s.stay_number
				if (active_filters.prepared && !location_ready_map[sn])
					return false
				if (active_filters.to_prepare && location_ready_map[sn])
					return false
				if (active_filters.arrived && !onsite_map[sn])
					return false
				if (active_filters.not_arrived && onsite_map[sn])
					return false
				if (active_filters.precheckin && !_is_real_pc(s))
					return false
				if (active_filters.no_precheckin && _is_real_pc(s))
					return false
				if (active_filters.qr)
				{
					const rk = range_mode ? (s.arrival_datestring || '') : current_day_key
					if (!s.location || !qr_set.has(s.location + ':' + rk))
						return false
				}
				if (active_filters.user_app && !(s.users_count > 0))
					return false
				if (active_filters.late)
				{
					const ea = _eff_arrival(s)
					if (!ea || new Date(ea * 1000).getHours() < 19)
						return false
				}
				if (active_filters.with_options)
				{
					const has_opts = (kit_double_map[sn] || 0) > 0 || (kit_simple_map[sn] || 0) > 0 || (baby_beds_map[sn] || 0) > 0 || (this.state.kit_towels_map[sn] || 0) > 0 || (this.state.baby_chairs_map[sn] || 0) > 0
					if (!has_opts)
						return false
				}
				return true
			})
		}
		if (!this.state.show_camping)
			filtered = filtered.filter(s => s.stay_type !== 'Camping')
		if (!this.state.show_location)
			filtered = filtered.filter(s => s.stay_type !== 'Location')
		return (
			<>
				{confirm_msg && (
					<div className={`mb-4 flex items-center gap-2 rounded-md px-4 py-2 text-sm border ${confirm_type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
						<span>{confirm_type === 'success' ? '✓' : '✕'}</span>
						<span>{confirm_bold && <><strong>{confirm_bold}</strong>{' — '}</>}{confirm_msg}</span>
					</div>
				)}
				<div className="bg-white rounded-t-2xl border border-b-0 border-slate-200 shadow-sm">
				<div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<h3 className="text-lg font-bold text-slate-900 tracking-tight my-0">Liste des arrivées</h3>
						<span className="px-2 py-1 bg-blue-100 text-blue-600 text-sm font-bold rounded-lg uppercase tracking-widest">
							{filtered.length} {filtered.length > 1 ? 'Résultats' : 'Résultat'}
						</span>
					</div>
				</div>
				<div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
					<div className="flex items-center gap-3 flex-1 max-w-md">
						<div className="relative flex-1">
							<SearchIcon className="absolute left-3 h-4 w-4 text-slate-400" style={{ top: '50%', transform: 'translateY(-50%)' }} />
							<input
								type="text"
								placeholder="Rechercher un nom, un emplacement, un client..."
								value={this.state.search}
								onChange={e => this.setState({ search: e.target.value })}
								className="browser-default w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-indigo-400"
							/>
						</div>
						<div className="relative">
							<button
								onClick={() => this.setState(prev => ({ show_filters: !prev.show_filters }))}
								className={`p-2 bg-white border rounded-xl transition-all flex items-center gap-2 ${show_filters || active_filter_count > 0 ? 'border-indigo-400 text-indigo-600 shadow-sm' : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
								style={{ backgroundImage: 'none' }}
								title="Filtres"
							>
								<AdjustmentsIcon className="h-5 w-5" />
								{active_filter_count > 0 && (
									<span className="bg-indigo-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded-md leading-none">
										{active_filter_count}
									</span>
								)}
							</button>
							{show_filters && (
								<>
									<div className="fixed inset-0" style={{ zIndex: 19 }} onClick={() => this.setState({ show_filters: false })} />
									<motion.div
										initial={{ opacity: 0, y: 8, scale: 0.97 }}
										animate={{ opacity: 1, y: 0, scale: 1 }}
										transition={{ duration: 0.15 }}
										className="absolute left-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
										style={{ zIndex: 20, width: '17rem', top: '100%' }}
									>
										<div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
											<span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Filtres</span>
											{active_filter_count > 0 && (
												<button
													onClick={this._resetFilters}
													className="text-[10px] font-bold text-indigo-500 hover:text-indigo-700 uppercase tracking-tight"
													style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
												>
													Réinitialiser
												</button>
											)}
										</div>
										<div className="p-2">
											{[
												{ key: 'prepared',     label: 'Hébergement prêt',         icon: '🏠' },
												{ key: 'to_prepare',   label: 'À préparer',               icon: '🧹' },
												{ key: 'arrived',      label: 'Client arrivé',            icon: '✅' },
												{ key: 'not_arrived',  label: 'Non encore arrivé',        icon: '🚗' },
												{ key: 'precheckin',   label: 'Avec pré-checkin',         icon: '📋' },
												{ key: 'no_precheckin', label: 'Sans pré-checkin',        icon: '📭' },
												{ key: 'qr',           label: 'Quitté-Repris (Q-R)',      icon: '🔄' },
												{ key: 'user_app',     label: 'Utilisateur Guest App',    icon: '📱' },
												{ key: 'late',         label: 'Arrivée tardive (>18h)',   icon: '🌙' },
												{ key: 'with_options', label: 'Avec options (kits/bébé)', icon: '🛏' }
											].map(f => (
												<button
													key={f.key}
													onClick={() => this._toggleFilter(f.key)}
													className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all text-sm font-semibold ${active_filters[f.key] ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
													style={{ backgroundImage: 'none' }}
												>
													<div className="flex items-center gap-2.5">
														<span>{f.icon}</span>
														<span>{f.label}</span>
													</div>
													{active_filters[f.key] && (
														<div className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
													)}
												</button>
											))}
										</div>
									</motion.div>
								</>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						{[
							{ key: 'show_camping',  label: 'Emplacements' },
							{ key: 'show_location', label: 'Locatifs' }
						].map(({ key, label }) => (
							<div
								key={key}
								onClick={() => this.setState(prev => ({ [key]: !prev[key] }))}
								className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer select-none transition-all"
								style={{ backgroundImage: 'none' }}
							>
								<span className="text-xs font-bold text-slate-600">{label}</span>
								<div className={`w-8 h-4 rounded-full relative flex-shrink-0 transition-all duration-200 ${this.state[key] ? 'bg-indigo-500' : 'bg-slate-200'}`}>
									<div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-all duration-200 ${this.state[key] ? 'left-4' : 'left-0.5'}`} />
								</div>
							</div>
						))}
						<button
							onClick={() => this.setState(prev => ({ edit_mode: !prev.edit_mode }))}
							className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-white rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 transition-colors"
							style={{ backgroundColor: '#ffffff', backgroundImage: 'none' }}
						>
							{edit_mode ? 'Mode lecture' : 'Mode édition'}
						</button>
						<button
							onClick={this._downloadPdf}
							className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
							style={{ backgroundColor: 'transparent', backgroundImage: 'none' }}
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
							</svg>
							<span>Exporter PDF</span>
						</button>
					</div>
				</div>
			</div>
				<div className="overflow-x-auto border border-slate-200 rounded-b-2xl" style={{ borderTop: 'none', marginTop: '-1px' }}>
					<div className="flex flex-col" style={{ minWidth: '1000px' }}>
						<div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-600">
							<div className="col-span-2">Heure / Statut</div>
							<div className="col-span-3">Client</div>
							{show_kits && <div className="col-span-2">Options</div>}
							<div className={show_kits ? "col-span-3" : "col-span-5"}>Actions</div>
							{show_pms_check && <div className="col-span-1 text-center">Traité</div>}
							<div className={show_pms_check ? "col-span-1 text-right pr-4" : "col-span-2 text-right pr-4"}>Détails</div>
						</div>
						{filtered.length === 0
							? (
								<div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-4">
									<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
									<div className="text-center">
										<p className="font-semibold text-slate-600">Aucune arrivée trouvée</p>
										<p className="text-sm">Essayez de modifier vos filtres ou de changer de date</p>
									</div>
								</div>
							)
							: filtered.map((stay, k) => {
								const row_arrival_key = range_mode ? (stay.arrival_datestring || '') : current_day_key
								const is_qr = !!(stay.location && qr_set.has(stay.location + ':' + row_arrival_key))
								const is_location_ready = !!location_ready_map[stay.stay_number]
								const is_onsite = !!onsite_map[stay.stay_number]
								const is_jacuzzi = !!jacuzzi_map[stay.stay_number]
								const kit_double = kit_double_map[stay.stay_number] != null ? kit_double_map[stay.stay_number] : 0
								const kit_simple = kit_simple_map[stay.stay_number] != null ? kit_simple_map[stay.stay_number] : 0
								const baby_beds = baby_beds_map[stay.stay_number] != null ? baby_beds_map[stay.stay_number] : 0
								const kit_towels = this.state.kit_towels_map[stay.stay_number] != null ? this.state.kit_towels_map[stay.stay_number] : 0
								const baby_chairs = this.state.baby_chairs_map[stay.stay_number] != null ? this.state.baby_chairs_map[stay.stay_number] : 0
								const is_check_done = !!this.state.check_done_map[stay.stay_number]
								const location = location_map[stay.stay_number] != null ? location_map[stay.stay_number] : (stay.location || '')
								const pitch_options = location && pitch_list.findIndex(p => p.location === location) === -1
									? [{ location: location }, ...pitch_list]
									: pitch_list
								const is_notified = !!notif_map[stay.stay_number]
								const pc = stay.precheckin
								const client = stay.client || {}
								const lastname = (pc && pc.lastname) || client.lastname || '—'
								const firstname = (pc && pc.firstname) || client.firstname || ''
								const eff_arrival = _eff_arrival(stay)
								const displayed_time = eff_arrival !== null ? this._formatTime(eff_arrival) : '—'
								const is_real_pc = !!(pc && (pc.estimate_arrival || (pc.participants && pc.participants.length > 0)))
								const has_time = displayed_time !== '—'
								const status_label = is_onsite ? 'Arrivé' : (is_location_ready ? 'Prêt' : 'À préparer')
								const status_class = is_onsite ? 'bg-blue-100 text-blue-700 border-blue-200' : (is_location_ready ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200')
								const can_notify = is_location_ready && !is_onsite
								const arrival_date_label = row_arrival_key ? `${row_arrival_key.substring(8, 10)}/${row_arrival_key.substring(5, 7)}` : ''
								return (
									<motion.div
										key={stay.stay_number || k}
										initial={{ opacity: 0, y: 8 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.2, delay: k * 0.03 }}
										onClick={() => this.openModal(stay)}
										className={`group relative grid grid-cols-12 gap-4 items-center p-4 border-b border-slate-100 hover:bg-indigo-50 cursor-pointer transition-colors bg-white`}
									>
										<div className="col-span-2 flex flex-col gap-1.5">
											<div className={`flex items-center gap-1.5 font-bold text-base ${has_time ? 'text-slate-900' : 'text-amber-500'}`}>
												<ClockIcon className={`h-3.5 w-3.5 shrink-0 ${is_real_pc ? 'text-indigo-400' : (has_time ? 'text-slate-400' : 'text-amber-400')}`} />
												{edit_mode
													? (
														<select
															value={eff_arrival !== null ? eff_arrival : ''}
															onChange={e => {
																const v = e.target.value
																this._saveArrivalDashboard(stay.stay_number, v === '' ? '' : this._formatTime(parseInt(v, 10)), location)
															}}
															className="browser-default border border-indigo-300 rounded px-1 py-0.5 text-xs text-indigo-700 font-normal"
															onClick={e => e.stopPropagation()}
														>
															<option value=""></option>
															{ARRIVAL_TIME_OPTIONS.map(opt => (
																<option key={opt.value} value={opt.value}>{opt.label}</option>
															))}
														</select>
													)
													: <span>{displayed_time}</span>
												}
												{!has_time && !edit_mode && <ExclamationCircleIcon className="h-3 w-3 text-amber-400 shrink-0" />}
											</div>
											<div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border self-start ${status_class}`}>
												{status_label}
											</div>
											{range_mode && arrival_date_label && (
												<span className="text-[10px] text-indigo-600 font-semibold">{arrival_date_label}</span>
											)}
										</div>
										<div className="col-span-3 flex flex-col">
											<div className="flex items-center gap-2">
												{edit_mode
													? (
														<select
															value={location}
															onChange={e => this._saveLocation(stay.stay_number, e.target.value, location)}
															onClick={e => e.stopPropagation()}
															className="browser-default border border-indigo-300 rounded px-1 py-0.5 text-xs text-indigo-700 font-normal shrink-0"
															style={{ maxWidth: '110px' }}
														>
															<option value="">—</option>
															{pitch_options.map(p => (
																<option key={p.location} value={p.location}>{p.location}</option>
															))}
														</select>
													)
													: (location && (
														<span className="bg-black text-white text-xs font-bold px-1.5 py-0.5 rounded shadow-sm shrink-0">
															{location}
														</span>
													))
												}
												<span className="text-base font-semibold text-slate-900 truncate">
													{lastname !== '—' ? lastname.toUpperCase() : ''} {firstname}
												</span>
											</div>
											<div className="flex items-center gap-3 mt-1">
												{stay.location_category && (
													<span className="text-xs text-slate-500 truncate">{stay.location_category}</span>
												)}
												<div className="flex items-center gap-1.5 shrink-0">
													{is_qr && (
														<div className="flex items-center gap-1 bg-slate-50 text-rose-400 px-1.5 py-0.5 rounded-md border border-pink-200" title="Quitté-Repris (Ménage Prioritaire)">
															<RefreshIcon className="h-2.5 w-2.5 text-rose-400" />
															<span className="text-xs font-black uppercase tracking-tighter">Q-R</span>
														</div>
													)}
													{stay.users_count > 0 && (
														<div className="flex items-center gap-1 bg-green-50 text-green-600 px-1.5 py-0.5 rounded-md border border-green-100" title={`${stay.users_count} utilisateur${stay.users_count > 1 ? 's' : ''} sur la Guest App`}>
															<DeviceMobileIcon className="h-2.5 w-2.5" />
															<span className="text-xs font-black">{stay.users_count}</span>
														</div>
													)}
												</div>
											</div>
											{is_real_pc && (
												<span className="text-xs font-extrabold text-emerald-500 uppercase tracking-widest mt-1">Pré-check-in</span>
											)}
										</div>
										{show_kits && (
										<div className="col-span-2" onClick={e => e.stopPropagation()}>
											{edit_mode
													? (
														<div className="flex flex-col gap-1">
															<div className="flex items-center gap-1">
																<span className="text-[9px] text-slate-400 w-8">DBL</span>
																<input type="text" min="0" value={kit_double} onChange={e => this._onKitChange(stay.stay_number, 'kit_double', e.target.value)} onBlur={e => this._saveLinenField(stay.stay_number, 'kit_double', e.target.value, stay.location)} className="browser-default w-10 text-center border border-gray-300 rounded px-1 py-0.5 text-xs" />
															</div>
															<div className="flex items-center gap-1">
																<span className="text-[9px] text-slate-400 w-8">SGL</span>
																<input type="text" min="0" value={kit_simple} onChange={e => this._onKitChange(stay.stay_number, 'kit_simple', e.target.value)} onBlur={e => this._saveLinenField(stay.stay_number, 'kit_simple', e.target.value, stay.location)} className="browser-default w-10 text-center border border-gray-300 rounded px-1 py-0.5 text-xs" />
															</div>
															<div className="flex items-center gap-1">
																<span className="text-[9px] text-slate-400 w-8">BÉBÉ</span>
																<input type="text" min="0" value={baby_beds} onChange={e => this._onKitChange(stay.stay_number, 'baby_beds', e.target.value)} onBlur={e => this._saveLinenField(stay.stay_number, 'baby_beds', e.target.value, stay.location)} className="browser-default w-10 text-center border border-gray-300 rounded px-1 py-0.5 text-xs" />
															</div>
															<div className="flex items-center gap-1">
																<span className="text-[9px] text-slate-400 w-8">CHAISE</span>
																<input type="text" min="0" value={baby_chairs} onChange={e => this._onKitChange(stay.stay_number, 'baby_chairs', e.target.value)} onBlur={e => this._saveLinenField(stay.stay_number, 'baby_chairs', e.target.value, stay.location)} className="browser-default w-10 text-center border border-gray-300 rounded px-1 py-0.5 text-xs" />
															</div>
															<div className="flex items-center gap-1">
																<span className="text-[9px] text-slate-400 w-8">SERV</span>
																<input type="text" min="0" value={kit_towels} onChange={e => this._onKitChange(stay.stay_number, 'kit_towels', e.target.value)} onBlur={e => this._saveLinenField(stay.stay_number, 'kit_towels', e.target.value, stay.location)} className="browser-default w-10 text-center border border-gray-300 rounded px-1 py-0.5 text-xs" />
															</div>
														</div>
													)
													: (
														<div className="flex flex-wrap gap-1.5">
															{kit_double > 0 && <div className="flex items-center gap-0.5 bg-blue-50 text-blue-600 px-2 py-1 rounded-lg border border-blue-100"><span className="text-xs font-black">{kit_double}</span><span className="text-xs font-bold uppercase ml-0.5">K.DBL</span></div>}
															{kit_simple > 0 && <div className="flex items-center gap-0.5 bg-indigo-50 text-indigo-600 px-2 py-1 rounded-lg border border-indigo-100"><span className="text-xs font-black">{kit_simple}</span><span className="text-xs font-bold uppercase ml-0.5">K.SPL</span></div>}
															{kit_towels > 0 && <div className="flex items-center gap-0.5 bg-cyan-50 text-cyan-600 px-2 py-1 rounded-lg border border-cyan-100"><span className="text-xs font-black">{kit_towels}</span><span className="text-xs font-bold uppercase ml-0.5">K.SERV</span></div>}
															{baby_beds > 0 && <div className="flex items-center gap-0.5 bg-purple-50 text-purple-600 px-2 py-1 rounded-lg border border-purple-100"><span className="text-xs font-black">{baby_beds}</span><span className="text-xs font-bold uppercase ml-0.5">L.BÉBÉ</span></div>}
															{baby_chairs > 0 && <div className="flex items-center gap-0.5 bg-orange-50 text-orange-600 px-2 py-1 rounded-lg border border-orange-100"><span className="text-xs font-black">{baby_chairs}</span><span className="text-xs font-bold uppercase ml-0.5">CHAISE</span></div>}
															{kit_double === 0 && kit_simple === 0 && kit_towels === 0 && baby_beds === 0 && baby_chairs === 0 && (
																<span className="text-slate-300 text-xs">—</span>
															)}
														</div>
													)
											}
										</div>
										)}
										<div className={`${show_kits ? 'col-span-3' : 'col-span-5'} flex items-center gap-2`} onClick={e => e.stopPropagation()}>
											<button onClick={() => this._toggleLocationReady(stay.stay_number, is_location_ready, stay.location)} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all border ${is_location_ready ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/30'}`} style={{ backgroundImage: 'none' }}>
												<div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${is_location_ready ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
													{is_location_ready && <CheckCircleIcon className="h-2.5 w-2.5 text-white" />}
												</div>
												Prêt
											</button>
											<button onClick={() => this._toggleOnsite(stay.stay_number, is_onsite, stay.location)} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all border ${is_onsite ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50/30'}`} style={{ backgroundImage: 'none' }}>
												<div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${is_onsite ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
													{is_onsite && <CheckCircleIcon className="h-2.5 w-2.5 text-white" />}
												</div>
												Arrivé
											</button>
											{show_jacuzzi && (
												<button onClick={() => this._toggleJacuzzi(stay.stay_number, is_jacuzzi, stay.location)} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all border h-9 min-w-[52px] ${is_jacuzzi ? 'bg-cyan-50 border-cyan-200 text-cyan-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-cyan-300 hover:bg-cyan-50/30'}`} style={{ backgroundImage: 'none' }}>
													<div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${is_jacuzzi ? 'bg-cyan-500 border-cyan-500' : 'border-slate-300'}`}>
														{is_jacuzzi && <CheckCircleIcon className="h-2.5 w-2.5 text-white" />}
													</div>
													JAZ
												</button>
											)}
											<button
												disabled={!can_notify || is_notified}
												onClick={() => this._onRequestNotify(stay)}
												title={is_notified ? 'Notification déjà envoyée' : (!is_location_ready ? "Marquez l'emplacement comme Prêt d'abord" : (is_onsite ? 'Le client est déjà arrivé' : 'Notifier le client'))}
												className={`p-2 rounded-lg border shrink-0 h-9 w-9 flex items-center justify-center transition-all ${is_notified ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : (can_notify ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700 shadow-md' : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed')}`}
												style={{ backgroundImage: 'none' }}
											>
												<BellIcon className="h-4 w-4" />
											</button>
										</div>
										{show_pms_check && (
										<div className="col-span-1 flex items-center justify-center border-l border-slate-50" onClick={e => e.stopPropagation()}>
											<button onClick={() => this._toggleCheckDone(stay.stay_number, !is_check_done, stay.location)} title={is_check_done ? 'Traité' : 'Marquer comme traité'} className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all border shrink-0 ${is_check_done ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30'}`} style={{ backgroundImage: 'none' }}>
												<div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${is_check_done ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}`}>
													{is_check_done && <CheckCircleIcon className="h-2.5 w-2.5 text-white" />}
												</div>
											</button>
										</div>
									)}
										<div className={show_pms_check ? "col-span-1 flex justify-end items-center" : "col-span-2 flex justify-end items-center"}>
											<div className="text-slate-300 group-hover:text-indigo-500 transition-colors">
												<InformationCircleIcon className="h-5 w-5" />
											</div>
										</div>
									</motion.div>
								)
							})
						}
					</div>
				</div>
			</>
		)
	}

	_renderDateNav = () => {
		const { day, month, year, range_mode, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year, available_years } = this.state
		const _toggleRange = () => this.setState(prev => {
			if (prev.range_mode)
				return { range_mode: false, day: prev.range_start_day, month: prev.range_start_month, year: prev.range_start_year }
			const start = { day: prev.day, month: prev.month, year: prev.year }
			const start_key = `${start.year}-${start.month}-${start.day}`
			const end_key = `${prev.range_end_year}-${prev.range_end_month}-${prev.range_end_day}`
			const clamped_end = end_key < start_key ? start : { day: prev.range_end_day, month: prev.range_end_month, year: prev.range_end_year }
			return {
				range_mode: true,
				range_start_day: start.day,
				range_start_month: start.month,
				range_start_year: start.year,
				range_end_day: clamped_end.day,
				range_end_month: clamped_end.month,
				range_end_year: clamped_end.year
			}
		})
		if (!range_mode)
		{
			const { day_counts, nav_center_day, nav_center_month, nav_center_year } = this.state
			const selected_key = `${year}-${month}-${day}`
			const day_abbr = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
			const tiles = []
			for (let i = -2; i <= 2; i++)
			{
				const d = new Date(parseInt(nav_center_year), parseInt(nav_center_month) - 1, parseInt(nav_center_day))
				d.setDate(d.getDate() + i)
				const d_day = String(d.getDate()).padStart(2, '0')
				const d_month = String(d.getMonth() + 1).padStart(2, '0')
				const d_year = String(d.getFullYear())
				const d_key = `${d_year}-${d_month}-${d_day}`
				const is_selected = d_key === selected_key
				const count = day_counts[d_key] !== undefined ? day_counts[d_key] : '×'
				tiles.push(
					<button
						key={i}
						onClick={() => this.setState({ day: d_day, month: d_month, year: d_year })}
						className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[75px] h-16 py-10 px-6 rounded-xl border transition-all ${
							is_selected
								? 'border-indigo-300 shadow-md scale-105 z-10'
								: 'border-transparent hover:border-gray-200'
						}`}
						style={{ backgroundColor:'#ffffff', backgroundImage: 'none' }}
					>
						<span className={`text-xs font-bold tracking-widest mb-0.5 ${is_selected ? 'text-indigo-500' : 'text-gray-400'}`}>
							{day_abbr[d.getDay()]}.
						</span>
						<span className={`text-lg font-black leading-none ${is_selected ? 'text-indigo-500' : 'text-gray-900'}`}>
							{d_day}
						</span>
						<span className={`text-[8px] font-normal ${is_selected ? 'text-indigo-300' : 'text-gray-400'}`}>
							{count > 0 ? count : '—'}
						</span>
					</button>
				)
			}
			return (
				<div className="flex items-center gap-2 mb-4">
					<button
						onClick={() => {
							const t = this._getTodayParts()
							this.setState({ day: t.day, month: t.month, year: t.year, nav_center_day: t.day, nav_center_month: t.month, nav_center_year: t.year, range_mode: false })
						}}
						className="px-3 py-1.5 rounded-lg border border-gray-200 text-[10px] font-bold text-indigo-500 hover:bg-indigo-50 hover:border-indigo-200 transition-all whitespace-nowrap"
						style={{ backgroundColor: '#ffffff', backgroundImage: 'none' }}
					>
						AUJOURD'HUI
					</button>
					<div className="flex-1 min-w-0 flex items-center gap-1">
						<button
							onClick={() => this._shiftWeek(-1)}
							className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-300 transition-all flex-shrink-0"
							style={{ backgroundColor: '#ffffff', backgroundImage: 'none' }}
							title="Semaine précédente"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
							</svg>
						</button>
						<div className="flex items-center gap-4 overflow-x-auto px-2 py-2" style={{ scrollbarWidth: 'none' }}>
							{tiles}
						</div>
						<button
							onClick={() => this._shiftWeek(1)}
							className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-300 transition-all flex-shrink-0"
							style={{ backgroundColor: '#ffffff', backgroundImage: 'none' }}
							title="Semaine suivante"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
							</svg>
						</button>
					</div>
					<button
						onClick={_toggleRange}
						className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-gray-700 font-black shadow-sm hover:border-indigo-500 hover:text-indigo-500 transition-all group"
					>
						<CalendarIcon className="h-4 w-4 text-indigo-500" />
						<span className="uppercase text-sm font-bold tracking-widest whitespace-nowrap">Sélectionner une plage</span>
					</button>
				</div>
			)
		}
		const days_arr = []
		for (let d = 1; d <= 31; d++)
			days_arr.push(String(d).padStart(2, '0'))
		const months_arr = [
			{ value: '01', label: 'Janvier' }, { value: '02', label: 'Février' },
			{ value: '03', label: 'Mars' }, { value: '04', label: 'Avril' },
			{ value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
			{ value: '07', label: 'Juillet' }, { value: '08', label: 'Août' },
			{ value: '09', label: 'Septembre' }, { value: '10', label: 'Octobre' },
			{ value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' }
		]
		const selectCls = "block rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
		return (
			<>
				<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '1rem' }}>
					<button
						onClick={() => this._shiftRange(-1)}
						className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
					>
						&#8249;
					</button>
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<span className="text-sm font-medium text-gray-600">Du</span>
						<select value={range_start_day} onChange={e => this.setState({ range_start_day: e.target.value })} className={selectCls}>
							{days_arr.map(d => <option key={d} value={d}>{d}</option>)}
						</select>
						<select value={range_start_month} onChange={e => this.setState({ range_start_month: e.target.value })} className={selectCls}>
							{months_arr.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
						</select>
						<select value={range_start_year} onChange={e => this.setState({ range_start_year: e.target.value })} className={selectCls}>
							{available_years.map(y => <option key={y} value={y}>{y}</option>)}
						</select>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<span className="text-sm font-medium text-gray-600">Au</span>
						<select value={range_end_day} onChange={e => this.setState({ range_end_day: e.target.value })} className={selectCls}>
							{days_arr.map(d => <option key={d} value={d}>{d}</option>)}
						</select>
						<select value={range_end_month} onChange={e => this.setState({ range_end_month: e.target.value })} className={selectCls}>
							{months_arr.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
						</select>
						<select value={range_end_year} onChange={e => this.setState({ range_end_year: e.target.value })} className={selectCls}>
							{available_years.map(y => <option key={y} value={y}>{y}</option>)}
						</select>
					</div>
					<button
						onClick={() => this._shiftRange(1)}
						className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
					>
						&#8250;
					</button>
				</div>
				<div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
					<button
						onClick={_toggleRange}
						className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none"
					>
						<CalendarIcon className="h-4 w-4 text-gray-400" />
						Revenir au jour
					</button>
				</div>
			</>
		)
	}

	render()
	{
		const { loading } = this.state

		if (loading && this.state.loaded_key === null)
		{
			return (
				<div className="loader">
					<Loader />
				</div>
			)
		}

		const { copy_msg, day, month, year, range_mode, range_start_day, range_start_month, range_start_year, range_end_day, range_end_month, range_end_year } = this.state
		const day_names_full = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
		const month_names_full = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
		const subtitle_date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
		const day_subtitle = range_mode
			? (() => {
				const s = new Date(parseInt(range_start_year), parseInt(range_start_month) - 1, parseInt(range_start_day))
				const e = new Date(parseInt(range_end_year), parseInt(range_end_month) - 1, parseInt(range_end_day))
				const fmt = d => `${d.getDate()} ${month_names_full[d.getMonth()]} ${d.getFullYear()}`
				return `Du ${fmt(s)} au ${fmt(e)}`
			})()
			: `Planning du ${day_names_full[subtitle_date.getDay()]} ${subtitle_date.getDate()} ${month_names_full[subtitle_date.getMonth()]}`

		return (
			<div className="arrivals-root">
				<style>{`@keyframes arrivals-loading-bar { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } } .arrivals-root button:focus { background-color: inherit; outline: none; box-shadow: none; } .arrivals-root button:active { background-color: inherit; }`}</style>
				{loading && (
					<div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '3px', zIndex: 9999, background: 'linear-gradient(90deg, #6366f1 0%, #a5b4fc 50%, #6366f1 100%)', backgroundSize: '200% 100%', animation: 'arrivals-loading-bar 1.2s linear infinite' }} />
				)}
				{copy_msg && (
					<div style={{ position: 'fixed', bottom: '5%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none' }}>
						<div className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-white text-sm font-medium shadow-lg">
							<span>✓</span>
							<span>« {copy_msg} » copié dans le presse-papiers</span>
						</div>
					</div>
				)}
				{this._renderLocationReadyConfirm()}
				{this._renderOnsiteConfirm()}
				{this._renderNotifConfirm()}
				{this._renderModal()}
				<div className="mb-5" aria-label="Breadcrumb">
					<ol className="flex items-center gap-1 text-sm">
						<li className="flex items-center">
							<Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
								<HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
								<span className="sr-only">Accueil</span>
							</Link>
						</li>
						{this.pages.map((page, id) => (
							<li key={id} className="flex items-center">
								<span className="text-gray-300 mx-1 select-none">/</span>
								<Link to={page.href} className="text-gray-400 hover:text-gray-600 transition-colors" aria-current={page.current ? 'page' : undefined}>
									{page.name}
								</Link>
							</li>
						))}
					</ol>
				</div>
				<div className="flex items-end justify-between mb-8">
					<div>
						<h1 style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em' }} className="text-4xl font-bold text-gray-900 leading-none mt-0 mb-0">
							Arrivées journalières
						</h1>
						<p className="text-sm text-gray-400 mt-2 mb-0">
							{day_subtitle}
						</p>
					</div>
					<Link to="/checkins/config" className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 bg-white shadow-sm text-xs font-bold text-indigo-600 uppercase tracking-wider rounded-xl hover:bg-gray-50 transition-colors focus:outline-none">
						<CogIcon className="h-4 w-4" />
						Configuration
					</Link>
				</div>
				{this._renderDateNav()}
				{this._renderStats()}
				{this._renderTable()}
			</div>
		)
	}
}

export default StayArrivals
